/*
  DIREVULF — product page behaviour that sits on top of product.js.

  product.js owns variant resolution, price, availability and swapping the
  gallery to a variant's own media. Everything here is the surrounding page
  behaviour it has no opinion about:

    - the resting state the reference design starts in (nothing chosen yet,
      ADD TO BAG live, pressing it asks for a size)
    - the quantity <select>
    - bringing a variant's image into view in the desktop image grid
    - the mobile gallery's swipe dots
    - copy-link on the share row

  The "Others Also Bought" rail is NOT driven here. carousel.js owns every
  rail in the theme, including that one: it adopts the arrows this section's
  template renders and moves the rail through the single scroll path that
  suspends scroll-snap for the length of a deliberate move. Two files moving
  one element is what made that rail refuse to move at all.

  It never invents price, stock or availability. Anything shown about a
  variant is still what product.js read from Shopify.
*/
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  var MOBILE = '(max-width: 991px)';
  function isMobile() { return window.matchMedia(MOBILE).matches; }

  /* BRINGING SOMETHING INTO VIEW MUST NOT MOVE A PAGE THAT ALREADY SHOWS IT.

     scrollIntoView({ block: 'center' }) re-centres unconditionally, and it
     scrolls EVERY scrollable ancestor to do it. On this page the desktop
     image grid is not one: .pdp2-media__grid computes to overflow: visible
     with scrollHeight === clientHeight, so the only thing left for it to
     scroll is the document. Measured on the storefront, that turned a size
     press into a smooth several-hundred-pixel vertical glide of the whole
     page — the page appearing to move on its own.

     Two changes. The guard: something already on screen is left exactly where
     it is, and the page does not move at all. And 'nearest' rather than
     'center' for the rest, so a target that really is off screen is brought
     just far enough to be seen instead of being dragged to the middle.

     'smooth' is kept because it is a deliberate, shopper-initiated move, and
     the browser already downgrades it to instant for anyone who has asked for
     reduced motion. */
  function reveal(el) {
    if (!el || !el.getBoundingClientRect || !el.scrollIntoView) return;
    var rect = el.getBoundingClientRect();
    var height = window.innerHeight || document.documentElement.clientHeight || 0;
    /* Comfortably in view already — a margin rather than a hairline, so an
       element peeking in by a pixel still gets brought in properly. */
    if (rect.bottom > 48 && rect.top < height - 48) return;
    try { el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }
    catch (e) { el.scrollIntoView(); }
  }

  /* ------------------------------------------------------------------ *
   * Buy box
   * ------------------------------------------------------------------ */

  function initBuyBox(root) {
    var form = $('[data-dv-product-form]', root);
    if (!form) return;

    var groups = $$('.dv-variants__group', root);
    var submit = $('[data-dv-add]', root);
    var submitText = submit ? $('[data-dv-add-text]', submit) : null;
    var errorNode = $('[data-dv-size-error]', root);
    var idInput = $('input[name="id"][data-dv-variant-id]', form);

    var strings = {};
    var stringNode = $('[data-dv-product-strings]', root);
    if (stringNode) {
      try { strings = JSON.parse(stringNode.textContent); } catch (e) { strings = {}; }
    }

    /* True while the shopper simply has not chosen every option yet — which is
       not the same thing as a combination Shopify says is unavailable. */
    function incomplete() {
      for (var i = 0; i < groups.length; i++) {
        if (!$('.dv-size__input:checked', groups[i])) return true;
      }
      return false;
    }

    function hideError() { if (errorNode) errorNode.hidden = true; }

    function showError() {
      if (!errorNode) return;
      errorNode.hidden = false;
      var group = groups[0];
      for (var i = 0; i < groups.length; i++) {
        if (!$('.dv-size__input:checked', groups[i])) { group = groups[i]; break; }
      }
      reveal(group);
    }

    /* Whether ANY variant can be bought. A product with nothing left in any
       size has no resting "ADD TO BAG" state to return to: the page renders
       it as Sold out and disabled, and refine() used to switch it straight
       back to a live ADD TO CART on load. For such a product refine() now
       keeps it Sold out (product.js alone would say "Unavailable", which is
       the word for a missing combination, not an empty stockroom). */
    var anyAvailable = true;
    var variantNode = $('[data-dv-variant-data]', root);
    if (variantNode) {
      try {
        var all = JSON.parse(variantNode.textContent) || [];
        if (all.length) {
          anyAvailable = all.some(function (v) { return v && v.available; });
        }
      } catch (e) { anyAvailable = true; }
    }

    /* Runs after product.js's own change handler, so it only ever relaxes the
       "nothing chosen yet" state back to the reference's resting state. */
    function refine() {
      if (!submit) return;
      if (!anyAvailable) {
        submit.disabled = true;
        if (submitText && strings.sold_out) submitText.textContent = strings.sold_out;
        return;
      }
      if (!incomplete()) return;
      submit.disabled = false;
      if (submitText && strings.add_to_cart) submitText.textContent = strings.add_to_cart;
    }

    $$('.dv-size__input', root).forEach(function (input) {
      input.addEventListener('change', function () {
        hideError();
        refine();
      });
    });

    /* Capture phase on the form itself, so this decides before cart.js's
       document-level submit listener ever sees the event. */
    form.addEventListener('submit', function (event) {
      if (!incomplete() && idInput && idInput.value) return;
      event.preventDefault();
      event.stopPropagation();
      showError();
    }, true);

    refine();
  }

  /* ------------------------------------------------------------------ *
   * Gallery: desktop grid scroll-into-view + mobile swipe dots
   * ------------------------------------------------------------------ */

  function initGallery(root) {
    var main = $('[data-dv-gallery-main]', root);
    if (!main) return;
    var cells = $$('.pdp2-media__cell', main);
    if (!cells.length) return;

    var dotWrap = $('[data-dv-gallery-dots]', root);
    var dots = [];

    if (dotWrap && cells.length > 1) {
      dotWrap.innerHTML = '';
      cells.forEach(function (cell, i) {
        var dot = document.createElement('span');
        dot.className = 'pdp2-media__dot' + (i === 0 ? ' is-active' : '');
        dotWrap.appendChild(dot);
        dots.push(dot);
      });
    }

    function activeIndex() {
      /* Horizontal on mobile; the desktop grid does not scroll horizontally,
         in which case every cell reports 0 and index 0 stays lit. */
      var mid = main.scrollLeft + (main.clientWidth / 2);
      var best = 0;
      var bestGap = Infinity;
      for (var i = 0; i < cells.length; i++) {
        var centre = cells[i].offsetLeft - main.offsetLeft + (cells[i].offsetWidth / 2);
        var gap = Math.abs(centre - mid);
        if (gap < bestGap) { bestGap = gap; best = i; }
      }
      return best;
    }

    function syncDots() {
      if (!dots.length) return;
      var index = activeIndex();
      for (var i = 0; i < dots.length; i++) dots[i].classList.toggle('is-active', i === index);
    }

    /* Not requestAnimationFrame-gated on purpose: the browser already fires
       scroll at most once a frame, and an rAF gate leaves the dots frozen
       wherever rAF does not run. */
    main.addEventListener('scroll', syncDots, { passive: true });

    syncDots();

    /* On desktop every image is already on the page, so a variant change
       should bring its own image into view rather than scroll a carousel.
       product.js already handles the horizontal (mobile) case. */
    var variants = [];
    var dataNode = $('[data-dv-variant-data]', root);
    if (dataNode) {
      try { variants = JSON.parse(dataNode.textContent) || []; } catch (e) { variants = []; }
    }
    var idInput = $('input[name="id"][data-dv-variant-id]', root);
    if (!idInput || !variants.length) return;

    var lastId = idInput.value;
    function bringIntoView() {
      if (isMobile()) return;
      if (idInput.value === lastId) return;
      lastId = idInput.value;
      var variant = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(idInput.value)) { variant = variants[i]; break; }
      }
      var mediaId = variant && variant.featured_media && variant.featured_media.id;
      if (!mediaId) return;
      reveal(document.getElementById('dv-media-' + mediaId));
    }

    $$('.dv-size__input', root).forEach(function (input) {
      /* After product.js has resolved and written the variant id. */
      input.addEventListener('change', function () { window.setTimeout(bringIntoView, 0); });
    });
  }

  /* ------------------------------------------------------------------ *
   * Share: copy link
   * ------------------------------------------------------------------ */

  function initShare(root) {
    var wrap = $('[data-dv-share]', root);
    if (!wrap) return;
    var button = $('[data-dv-share-copy]', wrap);
    var status = $('[data-dv-share-status]', wrap);
    if (!button) return;

    var url = wrap.getAttribute('data-share-url') || window.location.href;

    button.addEventListener('click', function () {
      /* One answer per press. Whichever route gets there first wins, and the
         others become no-ops, so the shopper is never told two things. */
      var settled = false;

      function announce(text) {
        if (settled) return;
        settled = true;
        if (!status) return;
        status.textContent = text;
        status.classList.add('is-on');
        window.setTimeout(function () {
          status.textContent = '';
          status.classList.remove('is-on');
        }, 2200);
      }

      function said() { announce(button.getAttribute('data-copied') || ''); }

      /* The older selection-and-execCommand route. It is not only for
         browsers without the Clipboard API: writeText REJECTS whenever the
         document is not focused, when the page is not a secure context, or
         when a permissions policy forbids it, and this is what runs then. */
      function legacyCopy() {
        var field = document.createElement('input');
        field.value = url;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();
        var ok = false;
        try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
        document.body.removeChild(field);
        if (ok) { said(); return true; }
        return false;
      }

      /* A copy control that silently does nothing is worse than one that is
         not there, and this one carries the Instagram workflow: copy the link,
         paste it into a post. So a rejected write falls through to the legacy
         route instead of being swallowed, and if BOTH fail the shopper is told
         to copy it themselves rather than being left with a dead button. */
      function failed() {
        if (settled) return;
        if (legacyCopy()) return;
        announce(button.getAttribute('data-copy-failed') || url);
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          navigator.clipboard.writeText(url).then(said, failed);
        } catch (e) {
          failed();
        }
        /* Chrome does not always settle this promise. Where the clipboard
           permission is gated — an embedded webview, an in-app browser, a
           pending prompt — writeText can stay PENDING for ever: it neither
           resolves nor rejects, so a plain .then(ok, fail) leaves the button
           silently dead. Measured in a real browser on this page: still
           pending after 1.5s with the document focused. This is the backstop,
           and `settled` means a write that lands first still wins. */
        window.setTimeout(failed, 1200);
        return;
      }

      failed();
    });
  }

  /* ------------------------------------------------------------------ *
   * Others Also Bought rail — deliberately not handled here
   *
   * This file used to move that rail itself, from its own click handlers on
   * .pdp2-also__nav, while carousel.js was mounted on the very same rail for
   * the indicator. Two engines on one scroll container, and the one here
   * asked for the move with:
   *
   *     rail.scrollBy({ left: cardWidth, behavior: 'auto' })
   *
   * 'auto' is not "instant" — per spec it defers to the element's computed
   * scroll-behavior, which the stylesheet sets to `smooth` on this rail. So
   * every press began a smooth animation while `scroll-snap-type: x mandatory`
   * was still switched on, the snap engine re-decided the resting place on
   * every frame of it, and the rail was pulled back to the card edge it had
   * started from. Measured on the storefront: one press ran
   * scrollBy({ left: 309 }) and rail.scrollLeft was still 0 afterwards.
   *
   * carousel.js now adopts the arrows this section already renders and drives
   * them through the one scroll path that suspends snapping for the length of
   * a deliberate move and targets a real snap point. Nothing here touches
   * that rail, so there is nothing left to fight with.
   * ------------------------------------------------------------------ */

  function initPdp(root) {
    if (!root || root.hasAttribute('data-dv-pdp-ready')) return;
    root.setAttribute('data-dv-pdp-ready', '');
    initBuyBox(root);
    initGallery(root);
    initShare(root);
  }

  function boot() { $$('[data-dv-product]').forEach(initPdp); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-dv-product]');
    if (root) initPdp(root);
  });
})();